import { z } from 'zod';
import type { PropertySubtype } from './property-taxonomy.schema';

// The ImmoScout24 Feintyp of a commercial Objekt, per Untertyp (IMM-1057.2).
// The tokens are ImmoScout24's own enum values. A token recurs across lists
// wherever ImmoScout24 offers it under several Untertypen, and means the same
// thing in each; the pair check keeps a value inside its Untertyp's list.
export const COMMERCIAL_FINE_TYPES_BY_SUBTYPE = {
  OFFICE_PRACTICE: [
    'LOFT',
    'STUDIO',
    'OFFICE',
    'OFFICE_FLOOR',
    'OFFICE_BUILDING',
    'OFFICE_CENTRE',
    'OFFICE_STORAGE_BUILDING',
    'SURGERY',
    'SURGERY_FLOOR',
    'SURGERY_BUILDING',
    'COMMERCIAL_CENTRE',
    'LIVING_AND_COMMERCIAL_BUILDING',
    'OFFICE_AND_COMMERCIAL_BUILDING',
  ],
  HOSPITALITY: [
    'BAR_LOUNGE',
    'CAFE',
    'CLUB_DISCO',
    'GUESTS_HOUSE',
    'TAVERN',
    'HOTEL',
    'HOTEL_RESIDENCE',
    'HOTEL_GARNI',
    'PENSION',
    'RESTAURANT',
    'BUNGALOW',
  ],
  HOTEL: ['HOTEL', 'HOTEL_RESIDENCE', 'HOTEL_GARNI', 'PENSION', 'GUESTS_HOUSE', 'BUNGALOW'],
  INDUSTRIAL: [
    'SHOWROOM_SPACE',
    'HALL',
    'HIGH_LACK_STORAGE',
    'INDUSTRY_HALL',
    'INDUSTRY_HALL_WITH_OPEN_AREA',
    'COLD_STORAGE',
    'MULTIDECK_CABINET_STORAGE',
    'STORAGE_WITH_OPEN_AREA',
    'STORAGE_AREA',
    'STORAGE_HALL',
    'SERVICE_AREA',
    'SHIPPING_STORAGE',
    'REPAIR_SHOP',
  ],
  SPECIAL_PURPOSE: [
    'RESIDENCE',
    'FARM',
    'HORSE_FARM',
    'VINEYARD',
    'REPAIR_SHOP',
    'LEISURE_FACILITY',
    'INDUSTRIAL_AREA',
    'SPECIAL_ESTATE',
    'COMMERCIAL_CENTRE',
  ],
} as const satisfies Partial<Record<PropertySubtype, readonly string[]>>;

export type CommercialFineType =
  (typeof COMMERCIAL_FINE_TYPES_BY_SUBTYPE)[keyof typeof COMMERCIAL_FINE_TYPES_BY_SUBTYPE][number];

export const COMMERCIAL_FINE_TYPES: readonly CommercialFineType[] = [
  ...new Set(Object.values(COMMERCIAL_FINE_TYPES_BY_SUBTYPE).flat()),
];

export const CommercialFineTypeSchema = z.enum(
  COMMERCIAL_FINE_TYPES as readonly [CommercialFineType, ...CommercialFineType[]],
);

// The one owner of „this Untertyp has a Feintyp": empty for a null Untertyp or
// one without a list.
export function commercialFineTypesFor(subtype: PropertySubtype | null): readonly CommercialFineType[] {
  if (subtype === null) return [];
  const lists: Partial<Record<PropertySubtype, readonly CommercialFineType[]>> = COMMERCIAL_FINE_TYPES_BY_SUBTYPE;

  return lists[subtype] ?? [];
}

export function isValidCommercialFineType(
  subtype: PropertySubtype | null,
  fineType: CommercialFineType | null,
): boolean {
  return fineType === null || commercialFineTypesFor(subtype).includes(fineType);
}
